import React, { useEffect, useRef, useState } from 'react';
import { PageLayout } from '../layouts/PageLayout';
import { PbFormBox } from '../components/PbFormBox';
import { PbFormBoxItem } from '../components/PbFormBoxItem';
import { ActionSheet, Button, InfiniteScroll, Input, List } from 'antd-mobile';
import { nanoid } from 'nanoid';
import { ManualDetail } from '../popups/ManualDetail';
import { addPopupStore } from '../stores/popup.store';
import { useRecoilState } from 'recoil';
import {
  Action,
  ActionSheetShowHandler,
} from 'antd-mobile/es/components/action-sheet';
import {
  IActionSheet,
  IOptionItem,
  IPopup,
  IApiResult,
} from '../interfaces/app.interface';

import * as ntcbApi from '../apis/ntcb.api';
import * as cmnApi from '../apis/cmn.api';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * 게시판 > 메뉴얼
 * @constructor
 */

// 목록 데이터의 현재 페이지 번호를 정의함
let valCurrentPage: number = 0;

// 메뉴얼 유형을 정의함
let valManualType: string = '';

const ManualPage = () => {
  /**[Recoil]
   * addPopup = 추가할 팝업 저장소를 정의함
   */
  const [addPopup, setAddPopup] = useRecoilState<IPopup | null>(addPopupStore);
  /**[UseState]
   * 1. searchInput = 검색 입력을 정의함
   * 2. manualTypeActionSheet = 메뉴얼 유형 액션시트를 정의함
   * 3. listData = 목록 데이터를 정의함
   * 4. hasMoreListData = 추가로 불러올 목록 데이터의 여부를 정의함
   * 5. currentPage = 목록 데이터의 현재 페이지 번호를 정의함
   */
  const [searchInput, setSearchInput] = useState<string>(''); // 1
  const [manualTypeActionSheet, setManualTypeActionSheet] = // 2
    useState<IActionSheet>({
      value: '',
      item: [],
    });

  const [listData, setListData] = useState<any[]>([]); // 3
  const [hasMoreListData, setHasMoreListData] = useState<boolean>(true); // 4
  const [currentPage, setCurrentPage] = useState<number>(0); // 5

  /**[공통]
   * getCommonCode = 공통코드를 불러옴
   * getDetailCodesAll = 상세 공통코드를 불러옴
   */
  const getCommonCode = (
    code: string,
    defaultItem: IOptionItem | null = null,
    callback: Function = () => {},
  ) => {
    let tmpOptionItem: Action[] = [];
    cmnApi.getDetailCodesAll({ grpCodeId: code }).then((data: IApiResult) => {
      // 기본 아이템이 있으면 아이템 목록에 추가함
      if (defaultItem !== null) {
        tmpOptionItem.push({
          text: defaultItem.label,
          key: defaultItem.value,
        });
      }

      // 불러온 데이터를 아이템 목록에 추가함
      _.sortBy(data.data.list, ['sortRdr']).map((item: any, index: number) => {
        tmpOptionItem.push({
          text: item.dtlCodeDscr,
          key: item.dtlCodeId,
        });
      });

      // 콜백 함수를 실행함
      callback(tmpOptionItem);
    });
  };

  // 액션시트 참조를 정의함
  const actionSheetHandlerRef = useRef<ActionSheetShowHandler>();

  // 액션시트에서 선택한 아이템의 레이블을 불러옴
  const getActionSheetLabel = (actionSheet: IActionSheet) => {
    let tmpOptionItem: Action | undefined | null = _.find(actionSheet.item, {
      key: actionSheet.value,
    });

    return tmpOptionItem?.text || '';
  };

  /**[목록 구성하기]
   * getListData = 메뉴얼 항목을 불러옴
   * loadListData = 항목 데이터를 불러옴
   * handleListData_onClick = 목록의 행을 클릭함
   * loadMoreListData = 목록 데이터를 추가로 불러옴
   */
  const getListData = (
    paramCurrentPage: number = 1,
    callback: Function = () => {},
  ) => {
    ntcbApi
      .getManuals({
        currPageIdx: paramCurrentPage,
        mnlTypeCode: valManualType,
        pageSize: 30,
        searchKeyword: searchInput.trim(),
      })
      .then((data: IApiResult) => {
        if (data.data.list !== undefined) {
          callback(data.data.list);
        }
      });
  };

  const handleListData_onClick = (data: any) => {
    let tmpId: string = nanoid();
    // 팝업을 추가함
    setAddPopup({
      id: tmpId,
      title: data.ttl,
      content: <ManualDetail id={data.brdId} />,
      widthSizePercent: 100,
      heightSizePercent: 80,
      position: 'bottom',
      maskClick: true,
    });
  };

  // 목록 데이터를 추가로 불러옴
  const loadMoreListData = async () => {
    // 불러올 페이지 번호를 증가함
    valCurrentPage += 1;

    // 목록을 불러옴
    getListData(valCurrentPage, (data: any[]) => {
      // 불러온 데이터를 목록 데이터에 추가함
      setListData((pre: any) => [...pre, ...data]);

      // 불러온 데이터가 있으면 추가로 불러올 목록 데이터가 존재한다고 적용함
      setHasMoreListData(data.length > 0);
    });
  };

  /**[검색 창]
   * handleSearchInput_onChange = 검색 입력의 값을 변경함
   * handleSearchButton_onClick = 검색 버튼을 클릭함
   */
  const handleSearchInput_onChange = (event: any) => {
    setSearchInput(event);
  };

  const handleSearchButton_onClick = () => {
    // 목록 데이터를 초기화함
    setListData([]);

    // 목록 데이터의 현재 페이지 번호를 초기화함
    valCurrentPage = 0;

    // 목록 데이터를 추가로 불러옴
    loadMoreListData();
  };

  /**[이벤트]
   * 페이지 로딩 후 한번만 실행함
   */
  useEffect(() => {
    // 공통코드를 불러옴
    getCommonCode(
      'MNL_TYPE',
      { label: '전체', value: '' },
      (item: Action[]) => {
        // 메뉴얼 유형 액션시트에 적용함
        setManualTypeActionSheet((pre: IActionSheet) => ({
          ...pre,
          item,
        }));
      },
    );

    // 목록 데이터를 추가로 불러옴
    loadMoreListData();

    return () => {};
  }, []);

  return (
    <PageLayout pageInfoBarLeftArea="메뉴얼">
      <PbFormBox label="검색">
        <div className="space-y-3">
          {/* 항목 */}
          <PbFormBoxItem label="메뉴얼 유형">
            {/* 버튼 */}
            <Button
              size="middle"
              fill="outline"
              className="w-full !border-indigo-200"
              onClick={() => {
                actionSheetHandlerRef.current = ActionSheet.show({
                  actions: manualTypeActionSheet.item,
                  onAction: (action: Action) => {
                    // 메뉴얼 유형 액션시트에 적용함
                    setManualTypeActionSheet((pre: IActionSheet) => ({
                      ...pre,
                      value: action.key.toString(),
                    }));

                    // 메뉴얼 유형에 적용함
                    valManualType = action.key.toString();

                    // 목록 데이터를 초기화함
                    setListData([]);

                    // 목록 데이터의 현재 페이지 번호를 초기화함
                    valCurrentPage = 0;

                    // 목록 데이터를 추가로 불러옴
                    loadMoreListData();

                    // 액션시트를 닫음
                    actionSheetHandlerRef.current?.close();
                  },
                });
              }}
            >
              <span className="text-md text-indigo-500">
                {getActionSheetLabel(manualTypeActionSheet)}
              </span>
            </Button>
          </PbFormBoxItem>

          {/* 항목 */}
          <PbFormBoxItem label="검색어">
            {/* 텍스트 입력 */}
            <Input
              placeholder="검색어를 입력하세요."
              clearable={true}
              value={searchInput}
              onChange={handleSearchInput_onChange}
              onEnterPress={handleSearchButton_onClick}
            />
          </PbFormBoxItem>

          {/* 항목 */}
          <PbFormBoxItem>
            {/* 버튼 */}
            <Button
              size="large"
              shape="default"
              className="w-full !bg-indigo-700"
              onClick={handleSearchButton_onClick}
            >
              <span className="text-md text-white">검색</span>
            </Button>
          </PbFormBoxItem>
        </div>
      </PbFormBox>

      {/* 목록 폼 박스 */}
      <List className="!rounded-lg overflow-hidden">
        {listData.map((item: any, index: number) => (
          <List.Item key={index}>
            <div
              onClick={() => handleListData_onClick(item)}
              className="button-event"
            >
              {/* 제목 */}
              <div className="">
                <span className="text-base text-gray-700">{item.ttl}</span>
              </div>

              {/* 작성일시  */}
              <div className="flex justify-between items-center">
                <div className="">
                  <span className="text-xs text-indigo-600 font-bold">
                    {item.mnlTypeName}
                  </span>
                </div>

                <div className="truncate space-x-1">
                  <span className="text-xs text-gray-400">{item.rgstDate}</span>
                </div>
              </div>
            </div>
          </List.Item>
        ))}
      </List>

      {/* 더 불러오기 */}
      <div className="flex justify-center items-center">
        <div
          onClick={loadMoreListData}
          className="translateZ button-event px-10 py-2 space-y-1 animate-pulse"
        >
          <div className="flex justify-center items-center">
            <span className="text-xs text-gray-500">더 불러오기</span>
          </div>

          <div className="flex justify-center items-center">
            <FontAwesomeIcon
              icon={['fas', 'chevron-down']}
              className="w-3 h-3 text-gray-500"
            />
          </div>
        </div>
      </div>

      {/*<InfiniteScroll*/}
      {/*  loadMore={loadMoreListData}*/}
      {/*  hasMore={hasMoreListData}*/}
      {/*  className="!h-1"*/}
      {/*>*/}
      {/*  <span className="text-xs text-gray-400 !h-1">*/}
      {/*    불러올 데이터가 더 이상 없습니다.*/}
      {/*  </span>*/}
      {/*</InfiniteScroll>*/}
    </PageLayout>
  );
};
export default ManualPage;
